import { useState, useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Image, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { uploadVfxShotThumbnail, deleteVfxShotThumbnail, getVfxShotThumbnailUrl } from '@/lib/storage'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import type { VfxShot } from '@/types/database'

interface VfxShotThumbnailUploadProps {
  vfxShot: VfxShot
  projectId: string
}

export function VfxShotThumbnailUpload({ vfxShot, projectId }: VfxShotThumbnailUploadProps) {
  const thumbnailPath = vfxShot.custom_metadata?.thumbnail_path as string | undefined
  const [preview, setPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  // Load thumbnail URL on mount and when vfxShot changes
  useEffect(() => {
    async function loadThumbnail() {
      if (thumbnailPath) {
        const url = await getVfxShotThumbnailUrl(thumbnailPath)
        setPreview(url)
      }
    }
    loadThumbnail()
  }, [thumbnailPath])

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const result = await uploadVfxShotThumbnail(projectId, vfxShot.id, file)

      const updatedMetadata = {
        ...vfxShot.custom_metadata,
        thumbnail_path: result.path,
      }

      const { error } = await supabase
        .from('vfx_shots')
        .update({ custom_metadata: updatedMetadata })
        .eq('id', vfxShot.id)

      if (error) throw error

      return result
    },
    onSuccess: (result) => {
      setPreview(result.url)
      queryClient.invalidateQueries({ queryKey: ['vfxShots', vfxShot.id] })
      queryClient.invalidateQueries({ queryKey: ['vfxShots', projectId] })
      toast.success('Thumbnail uploaded successfully')
    },
    onError: (error) => {
      console.error('Upload error:', error)
      toast.error('Failed to upload thumbnail')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (thumbnailPath) {
        await deleteVfxShotThumbnail(thumbnailPath)
      }

      const updatedMetadata = {
        ...vfxShot.custom_metadata,
      }
      delete updatedMetadata.thumbnail_path

      const { error } = await supabase
        .from('vfx_shots')
        .update({ custom_metadata: updatedMetadata })
        .eq('id', vfxShot.id)

      if (error) throw error
    },
    onSuccess: () => {
      setPreview(null)
      queryClient.invalidateQueries({ queryKey: ['vfxShots', vfxShot.id] })
      queryClient.invalidateQueries({ queryKey: ['vfxShots', projectId] })
      toast.success('Thumbnail removed successfully')
    },
    onError: (error) => {
      console.error('Delete error:', error)
      toast.error('Failed to remove thumbnail')
    },
  })

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB')
      return
    }

    uploadMutation.mutate(file)
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to remove this thumbnail?')) {
      deleteMutation.mutate()
    }
  }

  return (
    <>
      {preview ? (
        <Card className="relative w-48 h-32 overflow-hidden group">
          <img
            src={preview}
            alt="VFX shot thumbnail"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleUploadClick}
              disabled={uploadMutation.isPending || deleteMutation.isPending}
            >
              <Upload className="w-4 h-4 mr-1" />
              Replace
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              disabled={uploadMutation.isPending || deleteMutation.isPending}
            >
              <X className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </div>
        </Card>
      ) : (
        <Card
          className="w-48 h-32 border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-accent transition-colors"
          onClick={handleUploadClick}
        >
          <div className="text-center">
            <Image className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {uploadMutation.isPending ? 'Uploading...' : 'Click to upload'}
            </p>
          </div>
        </Card>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        disabled={uploadMutation.isPending || deleteMutation.isPending}
      />
    </>
  )
}
